import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, User, Flame, TrendingUp, MapPin } from 'lucide-react';
import { Proposicao } from '../types';
import './ProposicaoCardNew.css';

interface ProposicaoCardNewProps {
  proposicao: Proposicao;
}

const tipoColors: Record<string, { bg: string; color: string }> = {
  PEC: { bg: '#daf0ed', color: '#3d9a80' },
  PL: { bg: '#fef4a8', color: '#d9960c' },
  PLP: { bg: '#f3e5ff', color: '#7c4dff' },
  MPV: { bg: '#fdecea', color: '#c0392b' },
  PDL: { bg: '#e3f2fd', color: '#1a56a8' },
};

const tempColors: Record<string, string> = {
  baixa: 'temp-low',
  'média': 'temp-mid',
  alta: 'temp-high',
};

const impactoColors: Record<string, string> = {
  baixo: 'impacto-low',
  'médio': 'impacto-mid',
  alto: 'impacto-high',
};

function camaraUrl(externalId: string) {
  return `https://www.camara.leg.br/proposicoesWeb/fichadetramitacao?idProposicao=${externalId}`;
}

export default function ProposicaoCardNew({ proposicao }: ProposicaoCardNewProps) {
  const navigate = useNavigate();
  const colors = tipoColors[proposicao.tipo] || { bg: '#f0f0f0', color: '#666' };
  const prop = proposicao as any;
  const hasJabuti = prop.jabuti && prop.jabuti.level !== 'low';
  const isReal = proposicao.sync_source === 'camara' && proposicao.external_id;

  return (
    <div
      className={`prop-card ${hasJabuti ? `prop-card-jabuti-${prop.jabuti.level}` : ''}`}
      onClick={() => navigate(`/detail/${proposicao.id}`)}
    >
      {hasJabuti && (
        <div className={`prop-jabuti-banner jabuti-${prop.jabuti.level}`}>
          <div className="prop-jabuti-header">
            <span className="jabuti-icon">◈</span>
            <span className="jabuti-text">
              {prop.jabuti.level === 'high'
                ? 'Sinais relevantes identificados'
                : 'Sinais de atenção identificados'}
            </span>
            <span className="jabuti-score" title="Índice de Atenção Legislativa">
              IAL {Math.round(prop.jabuti.score)}
            </span>
          </div>
          {Array.isArray(prop.jabuti.indicators) && prop.jabuti.indicators.length > 0 && (
            <ul className="prop-jabuti-indicators">
              {prop.jabuti.indicators.map((ind: string, i: number) => (
                <li key={i}>{ind}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="prop-header">
        <div className="prop-badge" style={{ backgroundColor: colors.bg, color: colors.color }}>
          {proposicao.tipo}
        </div>
        <div className="prop-numero">{proposicao.numero}/{proposicao.ano}</div>
        <div className="prop-status" style={{ backgroundColor: colors.bg, color: colors.color }}>
          {proposicao.status}
        </div>
      </div>

      {(() => {
        const j = prop.jabuti || {};
        const identifier = `${proposicao.tipo} ${proposicao.numero}/${proposicao.ano}`;
        const llmTitle =
          (j.headline_com_autor as string | undefined) ||
          (j.headline as string | undefined) ||
          (prop.titulo_llm as string | undefined) ||
          '';
        const tituloFinal = llmTitle || identifier;
        const autores: any[] = Array.isArray(prop.autores) ? prop.autores : [];
        const proponentes = autores.filter((a) => a.proponente).slice(0, 2);
        const fallbackAutores = proponentes.length === 0 ? autores.slice(0, 2) : proponentes;
        const fallbackLabel = proposicao.autor || '?';
        const initial = (autores[0]?.nome || fallbackLabel).trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
        return (
          <>
            {tituloFinal && <h3 className="prop-title">{tituloFinal}</h3>}
            {(fallbackAutores.length > 0 || proposicao.autor) && (
              <div className="prop-author-row">
                <span className="prop-author-avatar">{initial}</span>
                <span className="prop-author-eyebrow">autor{fallbackAutores.length > 1 ? 'es' : ''}</span>
                <div className="prop-author-names">
                  {fallbackAutores.length === 0 ? (
                    <span className="prop-author-name">{fallbackLabel}</span>
                  ) : (
                    fallbackAutores.map((a, i) => (
                      <span key={i} className="prop-author-name">
                        {a.nome}
                        {a.partido && (
                          <span className="prop-author-party">
                            {' '}({a.partido}{a.uf ? `-${a.uf}` : ''})
                          </span>
                        )}
                      </span>
                    ))
                  )}
                  {autores.length > fallbackAutores.length && (
                    <span className="prop-author-more">+{autores.length - fallbackAutores.length}</span>
                  )}
                </div>
              </div>
            )}
            {proposicao.ementa && proposicao.ementa !== tituloFinal && (
              <p className="prop-ementa">{proposicao.ementa}</p>
            )}
          </>
        );
      })()}

      {(() => {
        const j = prop.jabuti;
        const temas: string[] = Array.isArray(j?.temas) ? j.temas : [];
        const publico: string[] = Array.isArray(j?.publico_alvo) ? j.publico_alvo : [];
        const temperatura: string | undefined = j?.temperatura;
        const impacto: string | undefined = j?.impacto_economico;
        const alcance: string | undefined = j?.alcance;
        const hasAny =
          temas.length > 0 || publico.length > 0 || temperatura || impacto || alcance;
        if (!hasAny) return null;
        return (
          <div className="prop-chips-wrap">
            <div className="prop-chips-eyebrow">TEMAS · CLASSIFICAÇÃO IA</div>
            <div className="prop-chips">
              {temas.map((t) => (
                <span key={`tema-${t}`} className="chip chip-tema">{t}</span>
              ))}
              {temperatura && (
                <span className={`chip chip-class ${tempColors[temperatura] || ''}`} title={prop.jabuti?.temperatura_motivo || 'Temperatura política'}>
                  <Flame size={11} /> <span className="chip-label">temp</span> {temperatura}
                </span>
              )}
              {impacto && (
                <span className={`chip chip-class ${impactoColors[impacto] || ''}`} title="Impacto econômico estimado">
                  <TrendingUp size={11} /> <span className="chip-label">impacto</span> {impacto}
                </span>
              )}
              {alcance && (
                <span className="chip chip-class chip-alcance" title="Alcance da proposição">
                  <MapPin size={11} /> <span className="chip-label">alcance</span> {alcance}
                </span>
              )}
              {publico.slice(0, 2).map((p) => (
                <span key={`pub-${p}`} className="chip chip-publico" title={`Atinge: ${p}`}>{p}</span>
              ))}
            </div>
          </div>
        );
      })()}

      <div className="prop-footer">
        <div className="prop-votes">
          <span className="icon">🗳️</span>
          <span className="value">{proposicao.numero_votacoes} votações</span>
        </div>
        <div className="prop-date">
          {new Date(proposicao.data_apresentacao).toLocaleDateString('pt-BR', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>
        {isReal && (
          <a
            href={camaraUrl(proposicao.external_id!)}
            target="_blank"
            rel="noopener noreferrer"
            className="prop-camara-link"
            onClick={(e) => e.stopPropagation()}
            title="Ver na Câmara dos Deputados"
          >
            <ExternalLink size={14} />
            camara.leg.br
          </a>
        )}
      </div>
    </div>
  );
}
